import { EXPLORE_ROUTES } from "../exploreRoutes";
import { GAMIFICATION_ROUTES } from "./gamificationRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import MyWalletSidebar from "../MyWalletSidebar";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import HeaderProfileSection from "../HeaderProfileSection";
import BattleInfoHeader from "./BattleInfoHeader";
import BattleConfirmationDialog from "./BattleConfirmationDialog";
import { shareFriendInvitation } from "./friendInvitation";
import GroupBattleCard from "./GroupBattleCard";
import CoinConverterGif from "../../assets/images/Coin-converter.gif";
import {
  buildGamificationFlowState,
  buildGamificationSubjectsState,
  getStoredGamificationFlowState,
} from "./gamificationFlowState";
import { getStoredUser } from "../../utils/authStorage";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useGamificationBattleSession } from "../../hooks/useGamificationBattleSession";
import "./GroupOpponentWaiting.css";
import "./GamificationLiveBattle.css";

const USER_STORAGE_BASE_URL = "https://storage.googleapis.com/vidyakasam-user/";

function resolveProfileImageUrl(value) {
  const rawValue = typeof value === "string" ? value.trim() : "";

  if (!rawValue) {
    return "";
  }

  if (/^(https?:|data:|blob:)/i.test(rawValue)) {
    return rawValue;
  }

  return `${USER_STORAGE_BASE_URL}${rawValue.replace(/^\/+/, "")}`;
}

const GroupOpponentWaiting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [shareNotice, setShareNotice] = useState("");
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(() => {
    const hasLocationState = Boolean(location.state && Object.keys(location.state).length);
    return buildGamificationFlowState(
      { ...(location.state || {}), entryMode: location.state?.entryMode || "random" },
      hasLocationState ? {} : getStoredGamificationFlowState()
    );
  }, [location.state]);
  const {
    state,
    startCountdownLeft,
    waitingCountdownLeft,
    joinedPlayers,
    requiredPlayers,
    confirmStart,
    cancelSearchConfirmation,
    dismissMatchFailure,
    leaveRoom,
    leaveQueueUser,
  } = useGamificationBattleSession(flowState);

  const selectedBattle = state?.selectedBattle || {};
  const selectedSubject = state?.selectedSubject || {};
  const selectedState = state?.selectedState || {};
  const profile = sidebarData?.user_data || getStoredUser() || {};
  const battleName =
    selectedBattle?.battles_name ||
    selectedBattle?.battle_name ||
    selectedBattle?.title ||
    "Group Battle";
  const battleDescription =
    selectedBattle?.battles_description ||
    selectedBattle?.battle_description ||
    selectedBattle?.description ||
    "Complete, Learn & improve Daily";
  const currentCoins = Number(state?.userCoins ?? state?.user_coins ?? 0);
  const entryFee = Number(
    selectedSubject?.game_entry_fee_group ??
      selectedSubject?.game_entry_fee ??
      selectedSubject?.gameEntryFee ??
      selectedBattle?.entry_fee ??
      state?.game_entry_fee ??
      0
  );
  const subjectName =
    state?.creator_subject_name ||
    selectedSubject?.subject_name ||
    selectedSubject?.title ||
    "Current Affairs";
  const stateName =
    state?.creator_state_name ||
    selectedState?.state_name ||
    selectedState?.name ||
    "Himachal Pradesh";
  const playerName = profile?.name || profile?.user_name || "Student";
  const playerImage = resolveProfileImageUrl(
    profile?.profile_pics ||
      profile?.user_profile ||
      profile?.profile_image ||
      profile?.image ||
      profile?.user_image ||
      profile?.avatar ||
      ""
  );
  const currentUserId = Number(profile?.user_id || profile?.id || 0);
  const isFriendRoom = state?.entryMode === "friend";
  const minimumFriendPlayers = 3;
  const hasEnoughFriendPlayers = (state?.users_list?.length || 0) >= minimumFriendPlayers;
  const canStartRoom = isFriendRoom && Boolean(state?.isRoomCreator) && hasEnoughFriendPlayers;
  const isChallengeReady = isFriendRoom
    ? hasEnoughFriendPlayers
    : requiredPlayers > 0 && joinedPlayers >= requiredPlayers;

  useEffect(() => {
    if (["gameplay", "waiting_for_answer"].includes(state?.liveStage) && state?.battle_room_id) {
      navigate(GAMIFICATION_ROUTES.liveBattle, { state });
    }
  }, [navigate, state]);

  useEffect(() => {
    if (state?.liveStage === "left") {
      navigate(EXPLORE_ROUTES.subjects, {
        state: buildGamificationSubjectsState(state),
      });
    }
  }, [navigate, state]);

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      if (!state?.start_confirmation_required) setShowLeaveModal(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [state?.start_confirmation_required]);

  const handleLeave = () => {
    if (state?.entryMode === "random" && state?.start_confirmation_required) {
      cancelSearchConfirmation();
      navigate(EXPLORE_ROUTES.subjects, { state: buildGamificationSubjectsState(state) });
      return;
    }
    if (state?.entryMode === "friend") {
      leaveRoom();
    } else {
      leaveQueueUser();
    }
    navigate(EXPLORE_ROUTES.subjects, {
      state: buildGamificationSubjectsState(state),
    });
  };

  const handleShareCode = async () => {
    if (!state?.room_code) return;
    try {
      const notice = await shareFriendInvitation({
        roomCode: state.room_code,
        categoryId: state.category_id,
        stateId: state.state_id,
      });
      setShareNotice(notice);
    } catch (_error) {
      setShareNotice("");
    }
  };

  const handleCopyCode = async () => {
    if (!state?.room_code) {
      return;
    }

    try {
      await navigator.clipboard.writeText(String(state.room_code));
      setShareNotice("Room code copied.");
    } catch (_error) {
      // Ignore clipboard failures.
    }
  };

  return (
    <>
      <HomeDashboardLayout showHeader={false} variant="battle-waiting-layout">
        <div className="qbl-page qbankList-page page-padding start-gamification">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page">
              <div className="categories-page-header mb-0">
                <div>
                  <button type="button" className="categories-back-button" onClick={() => setShowLeaveModal(true)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
                <div>
                  <h4 className="categories-page-title">{battleName}</h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            <div className="rapid-fire-header d-flex align-items-center justify-content-between group-header">
              <div>
                <h1 className="qb-title">{battleName}</h1>
                <p className="game-description">{battleDescription}</p>
              </div>
              <div>
                <div className="game-coin d-flex gap-2 justify-content-center align-items-center">
                  <span>
                    <img src={CoinConverterGif} alt="Coins" />
                  </span>
                  <span className="coin-valuee fw-bold">{currentCoins}</span>
                </div>
              </div>
            </div>

            {state?.socketError ? <div className="cs-status">{state.socketError}</div> : null}
            {isFriendRoom && state?.roomNotice ? <div className="cs-status" role="status">{state.roomNotice}</div> : null}
            {shareNotice ? <div className="cs-status" role="status">{shareNotice}</div> : null}
            {!state?.socketError && state?.connectionStatus === "connecting" ? <div className="cs-status">Connecting to battle...</div> : null}

            <BattleInfoHeader entryFee={entryFee} stateName={stateName} subjectName={subjectName} />
            <GroupBattleCard
              initialTime={waitingCountdownLeft}
              currentPlayerName={playerName}
              currentPlayerImage={playerImage}
              currentUserId={currentUserId}
              users={state?.users_list || []}
              roomCode={state?.room_code}
              entryMode={state?.entryMode}
              waitingCount={Math.max((requiredPlayers || 4) - 1, (state?.users_list?.length || 1) - 1, 3)}
              isChallengeReady={isChallengeReady}
              canStart={canStartRoom}
              onStart={confirmStart}
              onCopyCode={handleCopyCode}
              onShareCode={handleShareCode}
              onLeave={() => setShowLeaveModal(true)}
            />
          </div>
        </div>

        {state?.entryMode === "random" && state?.start_confirmation_required ? (
          <BattleConfirmationDialog
            secondsLeft={startCountdownLeft}
            confirmed={state?.start_confirmed}
            onConfirm={confirmStart}
            onExit={handleLeave}
            onClose={handleLeave}
          />
        ) : null}

        {state?.liveStage === "match_failed" ? (
          <div className="gamification-battle-modal">
            <div className="gamification-battle-modal__card" role="alertdialog" aria-modal="true" aria-labelledby="match-failed-title">
              <div className="battle-confirmation-icon"><i class="fa-solid fa-ban"></i></div>
             <h5 class="fw-bold blackText mb-2" id="match-failed-title">Battle unavailable</h5>
              <p>{state?.matchFailureMessage}</p>
              <div className="gamification-battle-modal__actions">
                <button type="button" className="gamification-battle-modal__primary" onClick={() => {
                  dismissMatchFailure();
                  navigate(EXPLORE_ROUTES.subjects, { state: buildGamificationSubjectsState(state) });
                }}>Back</button>
              </div>
            </div>
          </div>
        ) : null}

        {isFriendRoom && state?.opponent_room_left ? (
          <div className="gamification-battle-modal">
            <div className="gamification-battle-modal__card" role="alertdialog" aria-modal="true">
              <h3>Opponent left the room</h3>
              <p>A room member left. Return to the battle selection to join another room.</p>
              <div className="gamification-battle-modal__actions">
                <button type="button" className="gamification-battle-modal__primary" onClick={handleLeave}>Back</button>
              </div>
            </div>
          </div>
        ) : null}

        {showLeaveModal && !state?.start_confirmation_required && state?.liveStage !== "match_failed" && !state?.opponent_room_left ? (
          <div className="gamification-battle-modal">
            <div className="gamification-battle-modal__card">
                <div className="battle-confirmation-icon"><i class="bi bi-person-walking"></i></div>
              <h5 class="fw-bold blackText mb-2">{state?.entryMode === "friend" ? "Leave Room" : "Leave Queue"}</h5>
              <p>
                {state?.entryMode === "friend"
                  ? "If you leave now, your private room will be closed for you."
                  : "If you leave now, your matchmaking queue will be cancelled."}
              </p>
              <div className="gamification-battle-modal__actions">
                <button type="button" className="gamification-battle-modal__secondary" onClick={() => setShowLeaveModal(false)}>
                  Stay
                </button>
                <button type="button" className="gamification-battle-modal__primary" onClick={handleLeave}>
                  Leave
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <MyWalletSidebar withTopOffset={false} />
      </HomeDashboardLayout>
    </>
  );
};

export default GroupOpponentWaiting;




